import {
  DEFAULT_ROOM,
  LIMITS,
  STREAM,
  generateRoomCode,
  normalizeRoomName,
  type RoomUser,
} from "@yay/shared";

export const config = {
  // Dev talks to the local worker (ws://localhost:8787 — the same port the
  // vite /api proxy targets); prod falls back to the deployed worker. The
  // .env file must NOT set VITE_WS_URL, or it would pin BOTH environments to
  // whichever host was checked out last (this was the "Unknown message type"
  // trap: dev builds hitting the stale deployed worker).
  wsUrl:
    import.meta.env.VITE_WS_URL ??
    (import.meta.env.DEV
      ? "ws://localhost:8787/ws"
      : "wss://yay.wr7thful.workers.dev/ws"),
  hlsUrl: STREAM.HLS_URL,
  hlsFallback: STREAM.HLS_FALLBACK,
  maxUsernameLength: LIMITS.MAX_USERNAME_LENGTH,
  maxMessageLength: LIMITS.MAX_MESSAGE_LENGTH,
  maxChatMessages: LIMITS.MAX_CHAT_MESSAGES,
  usernameStorageKey: "yay_username",
  sessionStorageKey: "yay_session_id",
  identityStorageKey: "yay_identity",
  /** Per-tab flag: this document already joined. Survives reload/discard, not new tabs. */
  tabIdentifiedKey: "yay_tab_identified",
  /** Cloudflare Turnstile site key — empty disables the challenge. */
  turnstileSiteKey: import.meta.env.VITE_TURNSTILE_SITE_KEY ?? "",
} as const;

/** Current room from `?room=` (defaults to global). */
export function getRoomName(): string {
  if (typeof window === "undefined") return DEFAULT_ROOM;
  return normalizeRoomName(
    new URLSearchParams(window.location.search).get("room"),
  );
}

/** WebSocket URL scoped to a room. */
export function wsUrlForRoom(room: string): string {
  const u = new URL(config.wsUrl);
  u.searchParams.set("room", normalizeRoomName(room));
  return u.toString();
}

/**
 * HTTP URL for the cost-gate preflight (same host as the WebSocket — the
 * worker serves /api/capacity; the SPA checks it before opening a socket so
 * an at-capacity server shows the branded screen instead of a reconnect loop).
 */
export function capacityCheckUrl(): string {
  const u = new URL(config.wsUrl);
  u.protocol = u.protocol === "wss:" ? "https:" : "http:";
  u.pathname = "/api/capacity";
  u.search = "";
  return u.toString();
}

/** HTTP URL for the join overlay's password-required preflight. */
export function roomMetaUrl(room: string): string {
  const u = new URL(config.wsUrl);
  u.protocol = u.protocol === "wss:" ? "https:" : "http:";
  u.pathname = "/api/room";
  u.search = "";
  u.searchParams.set("room", normalizeRoomName(room));
  return u.toString();
}

/** App entry URL (`/`, optional `?room=`, or `?create`). */
export function buildAppUrl(options?: {
  room?: string;
  create?: boolean;
}): string {
  const url = new URL("/", window.location.origin);
  if (options?.create) {
    url.searchParams.set("create", "");
  } else if (options?.room) {
    const name = normalizeRoomName(options.room);
    if (name !== DEFAULT_ROOM) url.searchParams.set("room", name);
  }
  return `${url.pathname}${url.search}`;
}

/** Path-only app URL for landing-page link labels. */
export function displayAppUrl(options?: {
  room?: string;
  create?: boolean;
}): string {
  return buildAppUrl(options);
}

/** Navigate to a room (full reload so the socket reconnects cleanly). */
export function navigateToRoom(room: string): void {
  if (typeof window === "undefined") return;
  const url = new URL(buildAppUrl({ room }), window.location.origin);
  window.location.assign(url.toString());
}

/** Navigate to `/?create` so boot mints a fresh private room. */
export function navigateToCreate(): void {
  if (typeof window === "undefined") return;
  const url = new URL(buildAppUrl({ create: true }), window.location.origin);
  window.location.assign(url.toString());
}

/**
 * `?create` → mint a room code and rewrite to `?room=<code>`.
 * Rooms are created lazily when someone connects (`idFromName`).
 * Call once on boot before rendering the app.
 */
export function resolveCreateRoomRedirect(): boolean {
  if (typeof window === "undefined") return false;
  const url = new URL(window.location.href);
  if (!url.searchParams.has("create")) return false;
  url.searchParams.delete("create");
  url.searchParams.set("room", generateRoomCode());
  window.location.replace(url.toString());
  return true;
}

/* ------------------------------------------------------------------ */
/*  Session ID convergence across tabs                                */
/* ------------------------------------------------------------------ */

const SESSION_CHANNEL = "yay_session";
const SESSION_REQUEST_TIMEOUT_MS = 120;

let syncBc: BroadcastChannel | null = null;
let syncInit = false;

/**
 * One-time init: listen for session-id broadcasts and storage events so a
 * newly opened tab can adopt an existing sessionId instead of minting a
 * duplicate (which would create a second theater seat).
 */
export function initSessionSync(): void {
  if (syncInit || typeof window === "undefined") return;
  syncInit = true;

  try {
    syncBc = new BroadcastChannel(SESSION_CHANNEL);
    syncBc.onmessage = (event: MessageEvent) => {
      if (!event.data || typeof event.data !== "object") return;
      const data = event.data as { type?: string; id?: string };

      if (data.type === "session_id_request") {
        const stored = localStorage.getItem(config.sessionStorageKey);
        if (stored) {
          syncBc?.postMessage({ type: "session_id", id: stored });
        }
        return;
      }

      if (data.type === "session_id" && typeof data.id === "string" && data.id) {
        if (!localStorage.getItem(config.sessionStorageKey)) {
          try {
            localStorage.setItem(config.sessionStorageKey, data.id);
          } catch {
            /* storage unavailable */
          }
        }
      }
    };
  } catch {
    /* BroadcastChannel not available */
  }

  window.addEventListener("storage", onSessionStorage);

  // Mint (or adopt) a session id immediately on load — not only when the user
  // clicks Join — so a second tab opened during the overlay still inherits it.
  void getSessionId();
}

function onSessionStorage(e: StorageEvent): void {
  if (e.key !== config.sessionStorageKey || !e.newValue) return;
  if (!localStorage.getItem(config.sessionStorageKey)) {
    try {
      localStorage.setItem(config.sessionStorageKey, e.newValue);
    } catch {
      /* storage unavailable */
    }
  }
}

/** Ask other tabs if they already have a sessionId. Returns null on timeout. */
function requestExistingSession(): Promise<string | null> {
  return new Promise((resolve) => {
    let channel: BroadcastChannel | null = null;
    let resolved = false;

    const finish = (id: string | null) => {
      if (resolved) return;
      resolved = true;
      try {
        channel?.close();
      } catch {
        /* ignore */
      }
      resolve(id);
    };

    try {
      channel = new BroadcastChannel(SESSION_CHANNEL);
      channel.onmessage = (event: MessageEvent) => {
        if (event.data?.type === "session_id" && typeof event.data.id === "string") {
          try {
            localStorage.setItem(config.sessionStorageKey, event.data.id);
          } catch {
            /* storage unavailable */
          }
          finish(event.data.id);
        }
      };
      channel.postMessage({ type: "session_id_request" });
    } catch {
      finish(null);
      return;
    }

    setTimeout(() => finish(null), SESSION_REQUEST_TIMEOUT_MS);
  });
}

/**
 * Stable, persisted identity key. Generated once per browser and reused across
 * reconnects, reloads, and new tabs so the server can restore the same user
 * (name, color, avatar) instead of treating each connection as a new person.
 *
 * Reads from localStorage first. If empty, asks already-open tabs via
 * BroadcastChannel before generating a new ID — avoids the race where two
 * tabs both see empty storage and mint different sessionIds.
 */
export async function getSessionId(): Promise<string> {
  if (typeof window === "undefined") return crypto.randomUUID();

  const stored = localStorage.getItem(config.sessionStorageKey);
  if (stored && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(stored)) {
    return stored;
  }

  const existing = await requestExistingSession();
  if (existing) return existing;

  // Another tab may have written while we waited.
  const recheck = localStorage.getItem(config.sessionStorageKey);
  if (
    recheck &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(recheck)
  ) {
    return recheck;
  }

  const id = crypto.randomUUID();
  try {
    localStorage.setItem(config.sessionStorageKey, id);
  } catch {
    /* storage unavailable — still use in-memory id for this tab */
  }

  try {
    syncBc?.postMessage({ type: "session_id", id });
  } catch {
    /* ignore */
  }

  return id;
}

/**
 * Full visual identity, remembered in the browser. Sent on join so the server
 * can silently rebuild the same user even after long idle wiped its session.
 */
export function loadIdentity(): RoomUser | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(config.identityStorageKey);
    if (!raw) return null;
    const u = JSON.parse(raw) as Partial<RoomUser>;
    if (u && typeof u.username === "string" && typeof u.avatarSeed === "string") {
      return u as RoomUser;
    }
  } catch {
    /* ignore corrupt value */
  }
  return null;
}

export function saveIdentity(user: RoomUser): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(config.identityStorageKey, JSON.stringify(user));
    localStorage.setItem(config.usernameStorageKey, user.username);
  } catch {
    /* storage full / unavailable */
  }
}
